import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Split, LayoutGrid, Handshake, ShieldCheck, LogOut, User, Wallet as WalletIcon, Shield } from "lucide-react";
import NotificationsBell from "@/components/NotificationsBell";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLink = (to, label, Icon) => {
    const active = location.pathname === to;
    return (
      <Link
        to={to}
        data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
          active ? "bg-foreground text-background" : "text-foreground/70 hover:text-foreground hover:bg-secondary"
        }`}
      >
        <Icon className="h-4 w-4" />
        {label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-50 border-b border-black/10 bg-white/70 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 flex h-16 items-center justify-between">
        <Link to={user ? "/browse" : "/"} className="flex items-center gap-2" data-testid="nav-logo">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground hard-shadow-sm">
            <Split className="h-5 w-5" />
          </div>
          <span className="font-heading text-xl font-900 tracking-tight" style={{ fontWeight: 900 }}>
            BogoShare
          </span>
        </Link>

        {user ? (
          <nav className="flex items-center gap-1 sm:gap-2">
            {navLink("/browse", "Browse", LayoutGrid)}
            {navLink("/my-deals", "My Deals", Handshake)}
            {navLink("/wallet", "Wallet", WalletIcon)}
            {user.role === "admin" && navLink("/admin", "Admin", ShieldCheck)}
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-user-menu"
                  className="ml-1 flex h-9 w-9 items-center justify-center rounded-full border border-black/10 bg-secondary text-sm font-semibold hover:-translate-y-0.5 transition-transform duration-200"
                >
                  {user.name ? user.name.charAt(0).toUpperCase() : <User className="h-4 w-4" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-semibold truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-security" onClick={() => navigate("/security")}>
                  <Shield className="h-4 w-4 mr-2" /> Security
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-logout" onClick={handleLogout} className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="ghost" data-testid="nav-login-btn" onClick={() => navigate("/login")}>
              Log in
            </Button>
            <Button data-testid="nav-signup-btn" onClick={() => navigate("/signup")} className="rounded-full">
              Get started
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
